import dayjs from 'dayjs';
import { customRequestTypesEnums } from 'features/creativeFunctionality/models/customRequestsModel/data';
import { customProviderItemsFilter } from 'features/creativeFunctionality/data';
import { gameTypes } from 'features/creativeFunctionality/data';
import { normalizeProviderValue } from 'features/creativeFunctionality/models/shared';
import { getCurrentTimeZone } from 'shared/helpers';
// import { convertNotificationsCustom } from '../../../features/creativeFunctionality/models/notificationsCustomModel/convertNotificationsCustom';

const forbiddenProviders = ['gr8', 'digitain'];
const providerCategorySuffixes = new Set(['crash', 'live-casino', 'slots']);

const normalizeProvidersList = version => {
  let list = [];
  if (Array.isArray(version)) {
    list = version;
  } else if (Array.isArray(version?.providers)) {
    list = version.providers;
  } else if (Array.isArray(version?.items)) {
    list = version.items;
  } else if (typeof version === 'string') {
    list = [version];
  } else if (version && (version.provider || version.name)) {
    list = [version];
  }

  return list
    .map(item => {
      if (typeof item === 'string')
        return {
          provider: item,
        };
      if (item?.provider) return item;
      if (item?.name)
        return {
          provider: item.name,
        };
      return '';
    })
    .filter(Boolean)
    .filter(p => forbiddenProviders.findIndex(el => el === p.provider) < 0);
};

// Бэк может вернуть games_config который обернут еще одним уровнем games_config
// wh9le для обработки вложенности в вложенности
const normalizeGamesConfig = version => {
  if (!version) return null;
  let normalized = version;
  while (normalized?.games_config || normalized?.games) {
    normalized = normalized.games_config || normalized.games;
  }
  return normalized;
};

const isGameItem = item =>
  item && typeof item === 'object' && (item.game_id || item.gameId || item.img_url || item.name);

const flattenGameItems = value => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenGameItems);
  if (isGameItem(value)) return [value];
  if (typeof value !== 'object') return [];
  return Object.values(value).flatMap(flattenGameItems);
};

const getProviderIdentity = provider => `${provider.type}:${normalizeProviderValue(provider.value)}`;
const shouldPreferSplitProvider = (existingProvider, provider) =>
  !existingProvider.value.includes(':') && provider.value.includes(':');
const hasProviderCategorySuffix = providerValue => {
  const parts = (providerValue ?? '')
    .toString()
    .toLowerCase()
    .split(':');

  return parts.length > 1 && providerCategorySuffixes.has(parts[parts.length - 1].trim());
};
const getMatchedCustomProviders = providerValue => {
  const normalizedProvider = normalizeProviderValue(providerValue);
  const exactMatchesOnly = hasProviderCategorySuffix(providerValue);
  const matches = customProviderItemsFilter.filter(cp => {
    if (cp.value.toLowerCase() === providerValue.toLowerCase()) return true;
    if (exactMatchesOnly) return false;
    return normalizeProviderValue(cp.value) === normalizedProvider;
  });

  return Array.from(new Map(matches.map(match => [match.value.toLowerCase(), match])).values());
};

// Бэк присылает для одной категории и legacy ключ провайдера, и split ключ провайдера
// в этом случае оставляем одну запись и предпочитаем split ключ
const dedupeProvidersByIdentity = providers => {
  const uniqueProviders = new Map();

  providers.forEach(provider => {
    const identity = getProviderIdentity(provider);
    const existingProvider = uniqueProviders.get(identity);

    if (!existingProvider || shouldPreferSplitProvider(existingProvider, provider)) {
      uniqueProviders.set(identity, provider);
    }
  });

  return Array.from(uniqueProviders.values());
};

const convertProviders = data => {
  const newData = normalizeProvidersList(data.new_version);
  const oldData = normalizeProvidersList(data.prevision_version);

  const result = [];

  newData.forEach(newProvider => {
    const oldProvider = oldData.find(p => p.provider === newProvider.provider);

    let hasChanges = false;
    let changes = {};

    if (oldProvider === undefined) {
      hasChanges = true;
      changes = {
        isActive: {
          before: !newProvider.is_active,
          after: newProvider.is_active,
        },
        position: {
          before: null,
          after: newProvider.position,
        },
      };
    } else {
      // is_active
      if (oldProvider?.is_active !== undefined && oldProvider?.is_active !== newProvider.is_active) {
        changes.isActive = {
          before: oldProvider.is_active,
          after: newProvider.is_active,
        };
        hasChanges = true;
      }

      // position
      if (oldProvider?.position && oldProvider.position !== newProvider.position) {
        changes.position = {
          before: oldProvider.position,
          after: newProvider.position,
        };
        hasChanges = true;
      }
    }

    if (hasChanges)
      result.push({
        provider: newProvider,
        changes,
      });
  });

  return {
    providers: result,
  };
};

const convertGames = data => {
  const transformGamesConfig = (games, isOld) => {
    const normalizedGames = normalizeGamesConfig(games);
    if (!normalizedGames) return [];
    const gamesList = flattenGameItems(normalizedGames);

    return gamesList.filter(g => g && typeof g === 'object');
  };

  const oldGames = transformGamesConfig(data.prevision_version, true);
  const newGames = transformGamesConfig(data.new_version, false);

  const result = [];

  newGames.forEach(newGame => {
    const oldGame = oldGames.find(g => g.game_id === newGame.game_id);

    let hasChanges = false;
    let changes = {};

    if (oldGame === undefined) {
      hasChanges = true;
      changes = {
        isActive: {
          before: !newGame.is_enabled,
          after: newGame.is_enabled,
        },
        position: {
          before: null,
          after: newGame.display_order,
        },
      };
    } else {
      // is_active
      if (oldGame?.is_enabled !== undefined && oldGame.is_enabled !== newGame.is_enabled) {
        changes.isActive = {
          before: oldGame.is_enabled,
          after: newGame.is_enabled,
        };
        hasChanges = true;
      }

      // position
      if (oldGame?.display_order !== undefined && oldGame.display_order !== newGame.display_order) {
        changes.position = {
          before: oldGame.display_order,
          after: newGame.display_order,
        };
        hasChanges = true;
      }
    }
    if (hasChanges)
      result.push({
        game: {
          name: newGame.name,
          imgUrl: newGame.img_url,
          id: newGame.game_id,
          isActive: newGame?.is_enabled,
          position: newGame.display_order,
          provider: newGame.provider,
        },
        changes,
      });
  });

  return {
    games: result,
  };
};

const convertSocialMedia = data => {
  const transformSocialLinks = version => {
    const entries = Object.entries(version);
    if (entries.length === 0) return { social: '', link: '' };
    const [network, link] = entries[0];
    return {
      social: network,
      link: link || '',
    };
  };

  return {
    socialMedia: { old: transformSocialLinks(data.prevision_version), new: transformSocialLinks(data.new_version) },
  };
};

const getDetailsData = (type, data) => {
  switch (type) {
    case customRequestTypesEnums.providers:
      return convertProviders(data);

    case customRequestTypesEnums.game:
      return convertGames(data);

    case customRequestTypesEnums.socialMedia:
      return convertSocialMedia(data);

    case customRequestTypesEnums.notifications:
      return null;

    default:
      return null;
  }
};

class Converter {
  convertCustomGames(data) {
    const gamesConfig = normalizeGamesConfig(data);
    // Защита от неожиданного формата т.к ожидаемый объект { [gameType]: { [provider]: games[] } }.
    if (!gamesConfig || typeof gamesConfig !== 'object') return [];
    // Преобразуем вложенную структуру в правильный формат
    const games = Object.entries(gamesConfig).reduce((acc, [gameType, providers]) => {
      // Если провайдеры не объект, то пропускаем этот тип, чтобы не падать на Object.entries
      if (!providers || typeof providers !== 'object') return acc;
      Object.entries(providers).forEach(([providerName, providerGames]) => {
        const normalizedGames = Array.isArray(providerGames) ? providerGames : flattenGameItems(providerGames);
        const filteredGames = normalizedGames
          // В ответе иногда встречаются пустые или некорректные элементы, а мы отфильтровываем их
          .filter(g => g && typeof g === 'object')
          .map(g => ({
            name: g.name,
            value: g.game_id,
            type: gameType.replace('_', '-'),
            provider: String(providerName).toLowerCase(),
            isActive: g.is_enabled,
            position: g.display_order,
            imgUrl: g.img_url,
          }));
        acc.push(...filteredGames);
      });
      return acc;
    }, []);

    return games;
  }

  convertCustomRequests({ data, count }) {
    const requests = data.map(item => {
      const date = getCurrentTimeZone(dayjs(item.created_at));
      return {
        id: item.id,
        agentId: item.agent_id,
        username: item.agent_username,
        structure: item.structure ?? '',
        netwin: item.netwin ?? '0.00',
        type: item.type,
        createdAt: date,
        status: item.status,
        notificationBody: item.notification_body,
      };
    });
    return {
      items: requests,
      count: count,
    };
  }

  convertSingleCustomRequest(data) {
    try {
      const date = getCurrentTimeZone(dayjs(data.created_at));

      const requestType = data.type;

      return {
        meta: {
          id: data.id,
          agentId: data.agent_id,
          username: data.agent_username,
          structure: data.structure,
          netwin: data.netwin,
          createdAt: date,
          status: data.status,
        },
        type: requestType,
        ...getDetailsData(requestType, data),
        notifications: data.new_version.notification_body,
      };
    } catch (e) {
      alert(e);
    }
  }

  convertCustomProviders(data) {
    const typeOrder = Object.values(gameTypes);
    const providers = dedupeProvidersByIdentity(
      data.providers
        .flatMap(p => {
          const matches = getMatchedCustomProviders(p.provider);

          if (matches.length === 0) {
            return {
              value: p.provider,
              apiValue: p.provider,
              name: p.provider[0].toUpperCase() + p.provider.slice(1),
              position: p.position,
              isActive: p.is_active,
              disabled: p.disabled ?? false,
              type: gameTypes.slots,
            };
          }

          return matches.map(match => ({
            value: match.value,
            apiValue: p.provider,
            name: match.name,
            position: p.position,
            isActive: p.is_active,
            disabled: p.disabled ?? false,
            type: match.type,
          }));
        })
        .filter(p => forbiddenProviders.findIndex(el => el === (p.apiValue ?? p.value).toLowerCase()) < 0),
    ).sort((a, b) => {
      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
    return {
      items: providers,
      count: data.total,
    };
  }

  convertSocialMedia(data) {
    return {
      telegram: data.social_links.telegram ?? '',
      facebook: data.social_links.facebook ?? '',
      instagram: data.social_links.instagram ?? '',
    };
  }
}

export default Converter;
